import { FC, useRef, useState } from 'react';

import logger from '@eneco/common/log';
import { toURL } from '@eneco/common/path';
import { Recaptcha, RecaptchaRef } from '@eneco/components/Recaptcha/Recaptcha';
import { RecaptchaForm } from '@eneco/components/RecaptchaForm/RecaptchaForm';
import RichText from '@eneco/components/RichText/RichText';
import { typedYupResolver } from '@eneco/hooks/form';
import { useRouter } from '@eneco/next';
import { Button, Heading, NotificationBox, Stack, Text } from '@eneco/sparky';
import { wrap } from '@sitecore/common';
import { ContactFormRendering } from '@sitecore/types/ContactForm';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';

import BudgetManagerContactInputs from './Components/BudgetManagerContactInputs';
import ContactInputs from './Components/ContactInputs';
import InquiryInputs from './Components/InquiryInputs';
import { generateErrorType } from './util/error';
import { getSchema } from './util/schema';
import type { ErrorType, FormFields } from './util/types';
import { usePostInquiry } from './util/usePostInquiry';

const ContactForm: FC<ContactFormRendering> = ({ fields, params }) => {
  const { title, description } = fields.header;
  const canHaveBudgetManager = params?.budgetManagerAvailable === '1';

  const postInquiry = usePostInquiry();
  const { pushHref } = useRouter();

  const recaptchaRef = useRef<RecaptchaRef>(null);
  const [submittedFiles, setSubmittedFiles] = useState<File[]>([]);
  const [hasFileError, setHasFileError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorType, setErrorType] = useState<ErrorType | undefined>();

  const formSchema = getSchema(fields, canHaveBudgetManager);
  const methods = useForm<FormFields>({
    resolver: typedYupResolver<FormFields>(formSchema),
  });
  const {
    control,
    formState: { errors },
    register,
    watch,
  } = methods;
  const authorIsBudgetManager = !!watch('authorIsBudgetManager');
  const selectedTopic = watch('inquiryTopic');

  const submitForm: SubmitHandler<FormFields> = async input => {
    setIsSubmitting(true);
    // We keep the topic value for internal form logic, but we want to send the label to the backend
    const subject =
      fields.formFields.inquiryTopicFormField.value.options.find(topic => topic.value === selectedTopic)?.label || '';

    if (!hasFileError) {
      try {
        await postInquiry(input, submittedFiles, subject);
        setErrorType(undefined);
        pushHref(toURL(fields.footer.submitButtonLink.value.href));
      } catch (e) {
        logger.error('pqRD2C', 'Failed to submit form', { error: e });
        setErrorType(generateErrorType(e));
      }
      setIsSubmitting(false);
    }
  };

  const sharedProps = { fields, register, errors };

  return (
    <FormProvider {...methods}>
      <RecaptchaForm onSubmit={submitForm} recaptchaRef={recaptchaRef}>
        <Stack gap="10">
          <Stack gap="2">
            <Heading size="M" as="h2">
              {wrap(title)}
            </Heading>
            <Text size="BodyL">{wrap(description, <RichText html={description.value} />)}</Text>
          </Stack>
          <Stack gap="6">
            <ContactInputs
              control={control}
              authorIsBudgetManager={authorIsBudgetManager}
              canHaveBudgetManager={canHaveBudgetManager}
              {...sharedProps}
            />
            <InquiryInputs
              files={submittedFiles}
              setFiles={setSubmittedFiles}
              hasError={hasFileError}
              setHasError={setHasFileError}
              selectedTopic={selectedTopic}
              {...sharedProps}
            />
            {authorIsBudgetManager && <BudgetManagerContactInputs control={control} {...sharedProps} />}

            <Text size="BodyS">
              <Recaptcha hasError={!!errors.recaptchaToken} ref={recaptchaRef} />
            </Text>

            {errorType ? (
              <NotificationBox
                isAlert
                text={<RichText html={fields.errors[`${errorType}ErrorNotification`].value.content} />}
                title={fields.errors[`${errorType}ErrorNotification`].value.title}
                variant={fields.errors[`${errorType}ErrorNotification`].value.variant}
              />
            ) : null}
            <Stack.Item>
              <Button type="submit" isLoading={isSubmitting}>
                {fields.footer.submitButtonLink.value?.text}
              </Button>
            </Stack.Item>
          </Stack>
        </Stack>
      </RecaptchaForm>
    </FormProvider>
  );
};

export { ContactForm };
export default ContactForm;
